// The recording editor (AdminRecordings.dc.html's right-hand card): the Vimeo link with its title and
// length read from Vimeo (a typed Length when Vimeo can't be read), chapters with "What members will
// see", the summary, key messages, homework, files, tags, and Switch it on. Save changes saves without
// switching on, and saves a recording that's already live. The board's "Email ... when it goes live"
// box is gone (Adam, 5 Oct, stage 6: no notification emails): members hear through the bell, which the
// database writes the moment it's switched on. The saved line says so only when hub.bell_will_ring said
// so before the write (Adam, 5 Oct, stage 6's batch): never for 1 that's been live before.
// Owner, admin and support (support "pastes recordings"); files are owner and admin's, and so is
// "Switch it off" on a live recording, which asks first (Adam, 5 Oct, question 5).
// A course recording's own thumbnail (decision 47, as Adam changed it on 12 Oct: "exactly as a
// webinar's"), under the link: WebinarsView's block (ThumbnailBlock.jsx), picked, redrawn in the browser
// at up to 1280 by 720, uploaded straight away into the covers bucket in the product's own folder, and
// put on the recording with Save changes or Switch it on. Take it off saves none; the daily clear-up
// removes the file later. Owner and admin add, change and take it off; support sees the preview, as on
// Webinars. Not on the video (the masterclass), whose page is its own. Property Cashflow For Life's
// cards keep the course's own dark green module panel for a recording with no thumbnail (Adam, 12 Oct;
// products/ondemand/parts.jsx Thumb), so its preview draws that panel, not the date, and says so.
// The foot (Save changes, Switch it on, Switch it off and its question, and the lines that answer them)
// is pinned to the bottom of the screen while the editor shows (Adam, 13 Oct: Save sat about 900px
// under the thumbnail), and the editor tells the list through onDirty whether anything typed or picked
// isn't saved yet, so picking another row can ask first, and through onSaving whether a save is going
// on, so a row picked meanwhile waits for it.
import { useEffect, useId, useRef, useState } from 'react'
import { Button, ConfirmPanel, Field, TextArea, TextInput } from '../ui/index.js'
import { COPY } from './copy.js'
import { StateChip, LinkNote } from './Head.jsx'
import {
  audienceFor, headingFor, chaptersFromText, chaptersToText, readChapterLines, keyMessagesToText, homeworkShown,
  homeworkText, homeworkPlan, parseLength, tagsText, takesThumbnail,
} from './model.js'
import { useVimeoRead } from './vimeoRead.js'
import { saveRecording, attachFile, switchOffRecording, shrinkImage, uploadThumbnail } from './data.js'
import { ThumbnailBlock, uploaded, usePreviews } from './ThumbnailBlock.jsx'
import { thumbnailFileProblem } from './model.js'
import { recordingMs } from '../../product/recordingThumbs.js'
import { ModuleMark } from '../../products/ondemand/Pentagon.jsx'
import { numberedOf, thumbLabel } from '../../products/ondemand/model.js'
// the database's rule for tags, checked before the save (the mastermind build: 22023 too_many_tags, tag_too_long)
import { tagsProblem } from '../mastermind/words.js'

const lowerFirst = s => (s ? s[0].toLowerCase() + s.slice(1) : s)

export function eyebrowFor(item) {
  const { product, cohort, session } = item
  if (product.release_basis === 'own_intake' && session?.cohort_id && cohort) return `${product.name}, ${lowerFirst(cohort.name)}`
  return product.name
}

// Property Cashflow For Life's module panel, as its cards draw it where a recording has no thumbnail
// (products/ondemand/parts.jsx Thumb): the course's dark green, its module's side of the pentagon and
// the module's label in the corner ("Module 3, Part A", "Introduction"), for ThumbnailBlock's panel. Its
// look is ondemand.css's own (.od-thumb-mark, .od-thumb-label and the pentagon's), in the 1 stylesheet
// every page loads, with admin-content.css's .adc-thumb-frame--module for the green
export function modulePanel(item, data) {
  const own = { modules: (data.modules || []).filter(m => m.product_id === item.product.id) }
  const moduleId = item.recording?.module_id ?? item.session?.module_id ?? item.module?.id ?? null
  const module = own.modules.find(m => m.id === moduleId) || null
  const index = module ? numberedOf(own).findIndex(m => m.id === module.id) : -1
  // the module's side of the pentagon only on Property Cashflow For Life's 5 Ms, by its slug (the review of 13 Oct)
  const pentagon = item.product.slug === 'cashflow'
  return {
    className: 'adc-thumb-frame--module',
    node: (
      <>
        {pentagon && <span className="od-thumb-mark"><ModuleMark index={index} state="todo" size={null} dark /></span>}
        <span className="od-thumb-label">{thumbLabel(item.recording || { title: item.product.name }, module)}</span>
      </>
    ),
    none: COPY.recordingThumbnailNoneModule,
    broken: COPY.recordingThumbnailBrokenModule,
    brokenRead: COPY.recordingThumbnailBrokenReadModule,
  }
}

export function RecordingEditor({ item, data, api, role, personId, onSaved, onDirty, onSaving, flash: savedLine, clearFlash }) {
  const content = item.content || {}
  const recording = item.recording
  const live = Boolean(recording?.is_published)
  // a module item's homework is by its module, as a session's is (the product-types plan, 13 Oct)
  const moduleId = recording?.module_id ?? item.session?.module_id ?? item.module?.id ?? null
  const shown = homeworkShown({ recordingId: recording?.id || null, moduleId }, data.homework)
  const canFiles = role === 'owner' || role === 'admin'
  const canSwitchOff = live && (role === 'owner' || role === 'admin')
  const fiveThings = ['live_cohort', 'mastermind'].includes(item.product.type)
  const withThumb = takesThumbnail(item)
  const editsThumb = role === 'owner' || role === 'admin'
  const panel = item.product.type === 'on_demand' ? modulePanel(item, data) : null

  const [link, setLink] = useState(content.vimeo_url || '')
  const [lengthTyped, setLengthTyped] = useState('')
  const [needLength, setNeedLength] = useState(false)
  const [chapters, setChapters] = useState(chaptersToText(content.chapters))
  const [summary, setSummary] = useState(content.summary || '')
  const [keyMessages, setKeyMessages] = useState(keyMessagesToText(content.key_messages))
  const [homework, setHomework] = useState(homeworkText(shown))
  const [tags, setTags] = useState(tagsText(content.tags))
  const [busy, setBusy] = useState(null)
  const [problem, setProblem] = useState(null)
  const [flash, setFlash] = useState(null)
  const [askingOff, setAskingOff] = useState(false)
  const [offError, setOffError] = useState(null)
  const fileInput = useRef(null)
  const ids = useId()
  // the thumbnail: its path in the covers bucket (saved with the recording), and a picked 1's preview
  const savedPath = recording?.thumbnail_path || null
  const [thumbPath, setThumbPath] = useState(savedPath)
  const [picked, setPicked] = useState(null)
  const [thumbBusy, setThumbBusy] = useState(false)
  const [thumbNote, setThumbNote] = useState(null)
  const [cropped, setCropped] = useState(false)
  const savedThumb = !picked && Boolean(thumbPath) && thumbPath === savedPath
  const preview = usePreviews(api, withThumb && recording?.thumbnail_path ? [recording] : [], 'recordings')
  // the date members see on the grey panel: the day it was recorded, else its session's
  const when = recordingMs(recording || (item.session ? { session_id: item.session.id } : null), data.sessions)

  const vimeo = useVimeoRead(link, { savedUrl: content.vimeo_url, savedSeconds: content.duration_seconds })
  const lines = readChapterLines(chapters)
  const bad = lines.filter(l => !l.ok).length
  const files = recording ? data.placements.filter(p => p.recording_id === recording.id).length : 0
  const audience = audienceFor(item)
  const showLength = needLength || vimeo.state === 'failed' || (lengthTyped !== '' && vimeo.state !== 'known')

  const touch = setter => v => { setter(v); setFlash(null); setProblem(null); clearFlash?.() }

  // Anything not saved yet: a box changed from what it opened with, a length typed, or a thumbnail picked,
  // uploading, or taken off (1 picked and taken off again is back as it was)
  const [opened] = useState(() => ({ link, chapters, summary, keyMessages, homework, tags }))
  const dirty = link !== opened.link || lengthTyped !== '' || chapters !== opened.chapters || summary !== opened.summary
    || keyMessages !== opened.keyMessages || homework !== opened.homework || tags !== opened.tags
    || thumbPath !== savedPath || thumbBusy
  useEffect(() => { onDirty?.(dirty) }, [dirty, onDirty])
  useEffect(() => () => onDirty?.(false), [onDirty])
  // a save, a file or Switch it off going on, each ending in onSaved or a problem
  useEffect(() => { onSaving?.(Boolean(busy)) }, [busy, onSaving])
  useEffect(() => () => onSaving?.(false), [onSaving])

  // The length to save: the saved one for the same video, Vimeo's, or the one typed
  async function lengthToSave() {
    if (vimeo.state === 'known') return { seconds: vimeo.seconds, title: null }
    const typed = parseLength(lengthTyped)
    if (vimeo.state === 'found' || vimeo.state === 'reading' || vimeo.state === 'read' || vimeo.state === 'failed') {
      const answer = vimeo.seconds ? { seconds: vimeo.seconds, title: vimeo.title } : await vimeo.settled()
      if (answer?.seconds) return answer
      return { seconds: typed, title: null }
    }
    return { seconds: null, title: null }
  }

  // A picked image: checked, redrawn and uploaded at once, then saved on the recording with Save
  async function pickThumbnail(file) {
    if (!file || thumbBusy) return
    setProblem(null)
    setFlash(null)
    clearFlash?.()
    const bad = thumbnailFileProblem(file)
    if (bad) return setThumbNote({ text: bad, problem: true })
    setThumbBusy(true)
    setThumbNote(null)
    try {
      let image
      try { image = await shrinkImage(file) } catch { setThumbNote({ text: COPY.thumbnailUnreadable, problem: true }); return }
      const path = await uploadThumbnail(api, { productId: item.product.id, image })
      uploaded.set(path, image.preview)
      setThumbPath(path)
      setPicked(image.preview)
      setCropped(!image.sixteenNine)
      setThumbNote({ text: COPY.recordingThumbnailReady, problem: false })
    } catch (e) {
      setThumbNote({ text: api.words(e), problem: true })
    } finally {
      setThumbBusy(false)
    }
  }

  function takeThumbnailOff() {
    setProblem(null)
    setFlash(null)
    clearFlash?.()
    setThumbPath(null)
    setPicked(null)
    setCropped(false)
    setThumbNote(savedPath ? { text: COPY.recordingThumbnailTakenOff, problem: false } : null)
  }

  async function save(publish) {
    if (busy || thumbBusy) return
    setProblem(null)
    setFlash(null)
    clearFlash?.()
    const parsed = chaptersFromText(chapters, content.chapters || [])
    if (vimeo.state === 'bad') return setProblem(COPY.linkBad)
    if ((publish || live) && vimeo.state === 'empty') return setProblem(COPY.needLink)
    if (parsed.bad) return setProblem(COPY.chaptersNeedTimes)
    if (parsed.duplicate) return setProblem(COPY.chaptersSameTime(parsed.duplicate))
    if (moduleId && homeworkPlan(homework, shown, { productId: item.product.id, moduleId, recordingId: recording?.id }).tooLong) {
      return setProblem(COPY.homeworkTooLong)
    }
    if (lengthTyped.trim() && !parseLength(lengthTyped)) return setProblem(COPY.lengthBad)
    if (tagsProblem(tags)) return setProblem(tagsProblem(tags))
    setBusy(publish ? 'publish' : 'save')
    try {
      const { seconds, title } = await lengthToSave()
      if (vimeo.state !== 'empty' && !seconds && (publish || live)) {
        setNeedLength(true)
        setProblem(COPY.needLength)
        return
      }
      const form = {
        link: vimeo.state === 'empty' ? { url: null, id: null, hash: null } : { url: vimeo.url, id: vimeo.id, hash: vimeo.hash },
        seconds, chapters, summary, keyMessages, homework, tags,
        ...(withThumb && editsThumb ? { thumbnailPath: thumbPath } : {}),
      }
      // switching it on: whether the database will put it in the bell, asked before the write (a session
      // with no recording yet asks for the product)
      const rings = publish ? await api.bell_will_ring({ kind: 'new_recording', product: item.product.id, item: recording?.id || null }) : false
      const id = await saveRecording(api, { item, form, publish, personId, data, shownHomework: shown, vimeoTitle: title })
      const line = publish ? (rings ? COPY.liveNow(audience) : COPY.liveNoBell(audience)) : live ? COPY.savedLive : COPY.savedDraft
      await onSaved(id, line)
    } catch (e) {
      setProblem(api.words(e))
    } finally {
      setBusy(null)
    }
  }

  // Switch it off, once the confirm is answered: members stop seeing it, and progress is kept
  async function switchOff() {
    if (busy) return
    setOffError(null)
    setBusy('off')
    try {
      await switchOffRecording(api, { recordingId: recording.id, personId })
      setAskingOff(false)
      await onSaved(recording.id, COPY.switchedOff)
    } catch (e) {
      setOffError(api.words(e))
    } finally {
      setBusy(null)
    }
  }

  // A worksheet or pack: the picker opens from the press itself; a session with no recording yet is
  // saved as a draft first, so the file has a recording to sit on
  async function addFile(file) {
    if (!file || busy) return
    const section = data.sections.filter(s => s.product_id === item.product.id).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0]
    if (!section) return setProblem(COPY.fileNoSection(item.product.name))
    setProblem(null)
    clearFlash?.()
    setBusy('file')
    setFlash(COPY.fileAdding(file.name))
    try {
      let rec = recording
      if (!rec) {
        const { seconds, title } = await lengthToSave()
        const id = await saveRecording(api, {
          item, publish: false, personId, data, shownHomework: shown, vimeoTitle: title,
          form: {
            link: vimeo.state === 'found' || vimeo.state === 'read' || vimeo.state === 'failed' || vimeo.state === 'known'
              ? { url: vimeo.url, id: vimeo.id, hash: vimeo.hash } : { url: null, id: null, hash: null },
            seconds, chapters, summary, keyMessages, homework, tags,
            ...(withThumb && editsThumb ? { thumbnailPath: thumbPath } : {}),
          },
        })
        rec = { id, module_id: moduleId, session_id: item.session?.id || null }
      }
      const added = await attachFile(api, { file, product: item.product, recording: rec, section, personId })
      await onSaved(rec.id, COPY.fileAdded(added, item.name))
    } catch (e) {
      setFlash(null)
      setProblem(api.words(e))
    } finally {
      setBusy(null)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  return (
    <section className="ad-card adc-editor" aria-labelledby={`${ids}-h`}>
      <div className="adc-editor-head">
        <div className="adc-editor-title">
          <span className="adc-eyebrow">{eyebrowFor(item)}</span>
          <h2 className="adc-h2" id={`${ids}-h`} tabIndex={-1}>{headingFor(item)}</h2>
        </div>
        <StateChip chip={item.chip} big />
      </div>

      <Field label={COPY.link} plain>
        {props => (
          <TextInput {...props} value={link} onChange={touch(setLink)} placeholder={COPY.linkPlaceholder}
            aria-describedby={`${ids}-note`} autoComplete="off" spellCheck={false} />
        )}
      </Field>
      <LinkNote link={vimeo} id={`${ids}-note`} />
      {showLength && (
        <Field label={COPY.length} hint={COPY.lengthHint} plain className="adc-length">
          {props => <TextInput {...props} value={lengthTyped} onChange={touch(setLengthTyped)} placeholder={COPY.lengthPlaceholder} inputMode="numeric" />}
        </Field>
      )}

      {withThumb && (
        <ThumbnailBlock ids={ids} edits={editsThumb} path={thumbPath} url={picked || (savedThumb ? preview.url(recording) : null)}
          missing={savedThumb && preview.missing(recording)} when={when} panel={panel} busy={thumbBusy} note={thumbNote} cropped={cropped}
          onCropped={setCropped} onPick={pickThumbnail} onTakeOff={takeThumbnailOff}
          onError={savedThumb ? src => preview.failed(recording, src) : undefined}
          onLoad={savedThumb ? src => preview.loaded(recording, src) : undefined} />
      )}

      <div className="adc-two">
        <Field label={COPY.chapters} plain>
          {props => (
            <TextArea {...props} className="adc-chapters" value={chapters} onChange={touch(setChapters)} placeholder={COPY.chaptersPlaceholder} rows={8} spellCheck={false} />
          )}
        </Field>
        <div className="adc-preview-wrap">
          <span className="adc-field-label" id={`${ids}-pv`}>{COPY.preview}</span>
          <div className="adc-preview" aria-labelledby={`${ids}-pv`} role="list">
            {lines.map((c, i) => (
              <span className="adc-preview-line" key={i} role="listitem"><span className="adc-preview-time">{c.time}</span><span>{c.title}</span></span>
            ))}
            {lines.length === 0 && <span className="adc-preview-empty">{COPY.noChapters}</span>}
          </div>
          <span className="adc-count">{COPY.chapterCount(lines.length, bad)}</span>
        </div>
      </div>

      <Field label={COPY.summary} plain>
        {props => <TextArea {...props} value={summary} onChange={touch(setSummary)} placeholder={COPY.summaryPlaceholder} rows={3} />}
      </Field>

      {fiveThings && (
        <Field label={COPY.keyMessages} hint={COPY.keyMessagesHint} plain>
          {props => <TextArea {...props} value={keyMessages} onChange={touch(setKeyMessages)} placeholder={COPY.keyMessagesPlaceholder} rows={4} />}
        </Field>
      )}

      <div className={moduleId ? 'adc-two' : 'adc-one'}>
        {moduleId && (
          <Field label={COPY.homework} plain>
            {props => <TextArea {...props} className="adc-homework" value={homework} onChange={touch(setHomework)} placeholder={COPY.homeworkPlaceholder} rows={5} />}
          </Field>
        )}
        <div className="adc-files-tags">
          <span className="adc-field-label">{COPY.files}</span>
          {canFiles ? (
            <>
              <button type="button" className="adc-dashed" onClick={() => fileInput.current?.click()} disabled={Boolean(busy)}>
                {files ? COPY.filesAttached(files) : COPY.addFile}
              </button>
              <input ref={fileInput} type="file" className="adc-file-input" tabIndex={-1} aria-hidden="true"
                onChange={e => addFile(e.target.files?.[0])} />
            </>
          ) : (
            <span className="adc-quiet">{COPY.filesOwnerAdmin(files)}</span>
          )}
          <Field label={COPY.tags} plain className="adc-tags">
            {props => <TextInput {...props} value={tags} onChange={touch(setTags)} placeholder={COPY.tagsPlaceholder} />}
          </Field>
        </div>
      </div>

      <div className="adc-dock">
        <div className="adc-foot adc-foot--end">
          <span className="adc-foot-actions">
            <Button kind={live ? 'ink' : 'line'} busy={busy === 'save'} onClick={() => save(false)} disabled={Boolean(busy) || thumbBusy}>{COPY.saveChanges}</Button>
            {live
              ? <button type="button" className="ad-btn adc-btn-live" disabled>{COPY.itsLive}</button>
              : <Button kind="ink" busy={busy === 'publish'} onClick={() => save(true)} disabled={Boolean(busy) || thumbBusy}>{COPY.switchOn}</Button>}
            {canSwitchOff && (
              <Button onClick={() => { setAskingOff(true); setOffError(null); setFlash(null); clearFlash?.() }} disabled={Boolean(busy)}
                aria-expanded={askingOff}>{COPY.switchOff}</Button>
            )}
          </span>
        </div>
        {canSwitchOff && askingOff && (
          <div className="adc-confirm-wrap">
            <ConfirmPanel text={COPY.confirmOff(item.name)} confirmLabel={COPY.switchOff} cancelLabel={COPY.keepItOn}
              busy={busy === 'off'} error={offError} onConfirm={switchOff} onCancel={() => { setAskingOff(false); setOffError(null) }} />
          </div>
        )}
        {problem && <p className="ad-error" role="alert">{problem}</p>}
        {(flash || savedLine) && <p className="adc-flash" role="status">{flash || savedLine}</p>}
      </div>
    </section>
  )
}
